import RFS from '../console/_statics/riskfactors.statics';
import { getCowbellTranslations } from '../i18n/translations';
import type { GenericJsonApiSpecResponse } from '../types';

// Create a helper function that gets translations lazily
const getT = () => getCowbellTranslations().t;

export const CowbellFactorsMapping: { [key: string]: any } = {
  companyAggregate: {
    label: 'Company Aggregate',
    get tooltip() {
      return `${RFS.companyAggregateCF.description}`;
    },
    color: '#6ac52a',
    dataColor: '#6ac52a',
    remainingColor: '#bee6a2',
  },
  industryAggregate: {
    label: 'Industry Aggregate',
    get tooltip() {
      return `${RFS.industryAggregateCF.description} A higher score is better.`;
    },
    color: '#2180e2',
    dataColor: '#2180e2',
    remainingColor: '#b4d4f5',
  },
  networkSecurity: {
    color: '#6baedf',
    dataColor: '#6baedf',
    remainingColor: '#b4d4f5',
    label: 'Network Security',
    get subtext() {
      return `Measures the strength of the ${getT()(
        'organization'
      ).toLowerCase()}'s network infrastructure`;
    },
    get tooltip() {
      return `${RFS.networkSecurityCF.description} A higher score is better.`;
    },
  },
  cloudSecurity: {
    color: '#4dc8e8',
    dataColor: '#4dc8e8',
    remainingColor: '#b4d4f5',
    label: 'Cloud Security',
    get subtext() {
      return `${getT()(
        'organization'
      )}'s cloud security risk and use of public cloud / storage`;
    },
    get tooltip() {
      return `${RFS.cloudSecurityCF.description} A higher score is better.`;
    },
  },
  endpointSecurity: {
    color: '#83cebb',
    dataColor: '#83cebb',
    remainingColor: '#b4d4f5',
    label: 'Endpoint Security',
    subtext:
      'Endpoint (servers, mobile, IoT) vulnerabilities & security measures implemented',
    get tooltip() {
      return `${RFS.endpointSecurityCF.description} A higher score represents a higher level of preparedness.`;
    },
  },
  darkIntelligence: {
    color: '#0098db',
    dataColor: '#0098db',
    remainingColor: '#b4d4f5',
    label: 'Dark Intelligence',
    get subtext() {
      return `${getT()(
        'organization'
      )}'s exposure to darkweb (type and volume of data)`;
    },
    get tooltip() {
      return `${RFS.darkIntelligenceCF.description} A higher score represents less exposure.`;
    },
  },
  fundsTransfer: {
    color: '#7581e5',
    dataColor: '#7581e5',
    remainingColor: '#b4d4f5',
    label: 'Funds Transfer',
    subtext: 'Risk markers related to spear phishing (email, SMS, web)',
    get tooltip() {
      return `${RFS.fundsTransferCF.description} A higher score represents a lower level of risk.`;
    },
  },
  cyberExtortion: {
    color: '#91afd9',
    dataColor: '#91afd9',
    remainingColor: '#b4d4f5',
    label: 'Cyber Extortion',
    subtext:
      'Potential exposure to extortion-related attacks such as ransomware',
    get tooltip() {
      return `${RFS.cyberExtortionCF.description} A higher score represents a lower level of exposure.`;
    },
  },
  compliance: {
    color: '#00aea0',
    dataColor: '#00aea0',
    remainingColor: '#b4d4f5',
    label: 'Compliance',
    subtext:
      'Level of compliance to security standards (such as NIST, CSC-20, CIS)',
    get tooltip() {
      return `${RFS.complianceCF.description} A higher score is better.`;
    },
  },
  supplyChain: {
    label: 'Supply Chain',
    get tooltip() {
      return `Measures an ${getT()(
        'organization'
      ).toLowerCase()}'s susceptibility to software supply chain incidents. This factor is compiled from technographic and firmographic data, web scraping information and public exploits and vulnerability repositories. A higher score is better.`;
    },
    subtext: 'Visibility of risk exposure related to digital supply chain',
    color: '#00aea0',
    dataColor: '#00aea0',
    remainingColor: '#b4d4f5',
  },
};

export default CowbellFactorsMapping;

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null;

/**
 * Renames the risk service's `company` aggregate key to `companyAggregate`.
 *
 * The service answers **200 with `{ status: 'Not Found' }`** -- not a 404 --
 * for an account that has never been scored, so a body with no `details` is a
 * success response that reaches this transform. Reading it unguarded threw a
 * TypeError inside axios's transform chain, which surfaced as a retry storm, a
 * misleading "unable to retrieve risk data" toast, and a stuck loading state
 * on every surface reading account risk. Missing details normalize to `{}`:
 * callers destructure `details` and call `Object.keys` on it, so an empty
 * object keeps them on their ordinary "nothing to show" path.
 */
export function mapCFKeys({
  data,
}: GenericJsonApiSpecResponse<any>): Record<string, any> {
  if (!isPlainObject(data)) {
    return { details: {} };
  }

  if (!isPlainObject(data.details)) {
    return { ...data, details: {} };
  }

  const transformedDetails = Object.entries(data.details).reduce(
    (acc: any, currEntry: any) => {
      const [key, val] = currEntry;

      let newKey: string;
      switch (key) {
        case 'company':
          newKey = 'companyAggregate';
          break;
        default:
          newKey = key;
          break;
      }

      return { ...acc, [newKey]: val };
    },
    {}
  );

  return { ...data, details: transformedDetails };
}
